<script lang="ts">
  import { onMount } from 'svelte';
  import { push } from 'svelte-spa-router';
  import Table from '../../Table.svelte';
  import SearchInput from '../../SearchInput.svelte';
  import Pagination from '../../Pagination.svelte';
  import LoadingSkeleton from '../../LoadingSkeleton.svelte';
  import EmptyState from '../../EmptyState.svelte';
  import ErrorState from '../../ErrorState.svelte';
  import { CharityService, type Charity } from '../../../lib/api/admin/charity.service';
  import { createPreferencesManager } from '../../../lib/utils/preferences';
  import { formatDate } from '../../../lib/utils/formatters';

  const preferences = createPreferencesManager('unconfirmed-bank-details');

  let loading = true;
  let error: string | null = null;
  let charities: Charity[] = [];
  let totalItems = 0;
  let totalPages = 1;

  const savedFilters = preferences.getFilters();
  let searchQuery = savedFilters.searchQuery || '';
  let currentPage = preferences.getCurrentPage() || 1;
  let itemsPerPage = preferences.getPageSize() || 20;

  const tableColumns = [
    {
      key: 'name',
      label: 'Charity',
      width: '34%',
      mobilePrimary: true,
      render: (value: string) => `<span class="charity-name">${value}</span>`
    },
    {
      key: 'contact_email',
      label: 'Contact email',
      width: '28%',
      render: (value: string | null) =>
        value
          ? `<span class="contact-email">${value}</span>`
          : '<span class="text-muted">—</span>'
    },
    {
      key: 'updated_at',
      label: 'Last updated',
      width: '18%',
      render: (value: string | null) =>
        `<span class="date-value">${value ? formatDate(value) : '—'}</span>`
    },
    {
      key: 'bank_details_status',
      label: 'Status',
      width: '20%',
      align: 'center',
      render: () =>
        '<span class="bank-detail-badge unverified">Awaiting verification</span>'
    }
  ];

  async function loadCharities() {
    loading = true;
    error = null;

    try {
      const response = await CharityService.getCharities({
        page: currentPage,
        per_page: itemsPerPage,
        phrase: searchQuery || undefined,
        bank_details_verified: false,
        has_bank_details: true,
        sort_by: 'updated_at',
        sort_direction: 'desc'
      });

      if (response.success) {
        charities = response.data;
        totalItems = response.paging.total || charities.length;
        totalPages = response.paging.last_page || 1;
      } else {
        error = 'Failed to load charities';
      }
    } catch (err: unknown) {
      error = err instanceof Error ? err.message : 'Failed to load charities';
    } finally {
      loading = false;
    }
  }

  function handleSearch(event: CustomEvent<string>) {
    searchQuery = event.detail;
    currentPage = 1;
    preferences.update({ currentPage: 1, filters: { searchQuery } });
    loadCharities();
  }

  function handlePageChange(event: CustomEvent<{ page: number }>) {
    currentPage = event.detail.page;
    preferences.update({ currentPage });
    loadCharities();
  }

  function handleRowClick(row: Charity) {
    if (row?.uuid) {
      push(
        `/charity-profile/${row.uuid}?return=${encodeURIComponent('/admin/unconfirmed-bank-details')}`
      );
    }
  }

  function clearSearch() {
    searchQuery = '';
    currentPage = 1;
    preferences.update({ currentPage: 1, filters: { searchQuery: '' } });
    loadCharities();
  }

  onMount(() => {
    loadCharities();
  });
</script>

<div class="manage-list-page">
  <div class="page-header">
    <p class="subtitle">Charities with bank details on file that still need phone verification before payouts can proceed safely.</p>
  </div>

  <div class="filters-section">
    <SearchInput
      placeholder="Search charities..."
      bind:value={searchQuery}
      on:search={handleSearch}
      width="100%"
    />
  </div>

  <div class="table-section">
    {#if loading}
      <div class="loading-wrap">
        <LoadingSkeleton lines={8} height="2.5rem" />
      </div>
    {:else if error}
      <ErrorState message="Unable to load charities" description={error} onRetry={loadCharities} />
    {:else if charities.length === 0}
      <EmptyState
        message="No unconfirmed bank details"
        description={searchQuery
          ? 'Try adjusting your search to see more results.'
          : 'All charities with bank details on file have been verified.'}
        actionLabel={searchQuery ? 'Clear search' : null}
        onAction={searchQuery ? clearSearch : null}
      />
    {:else}
      <Table columns={tableColumns} data={charities} onRowClick={handleRowClick} />
      {#if totalPages > 1}
        <div class="pagination-container">
          <Pagination {currentPage} {totalPages} on:pageChange={handlePageChange} />
        </div>
      {/if}
    {/if}
  </div>
</div>

<style>
  .page-header {
    margin-bottom: 1.5rem;
  }

  .subtitle {
    margin: 0;
    font-size: 0.875rem;
    color: #6b7280;
    line-height: 1.5;
  }

  .filters-section {
    margin-bottom: 1rem;
    width: 100%;
  }

  .table-section {
    background: white;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    overflow: hidden;
  }

  .loading-wrap {
    padding: 2rem;
    min-height: 320px;
  }

  :global(.bank-detail-badge) {
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 500;
  }

  :global(.bank-detail-badge.unverified) {
    background: #fef3c7;
    color: #92400e;
  }

  :global(.charity-name) {
    font-weight: 500;
    color: #111827;
  }

  :global(.contact-email) {
    color: #374151;
    font-size: 0.875rem;
  }

  :global(.text-muted) {
    color: #9ca3af;
  }

  .pagination-container {
    padding: 1rem 1.5rem;
    border-top: 1px solid #f3f4f6;
  }
</style>
